import "@/index.css";
import "@/lib/i18n";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { StrictMode, useEffect } from "react";
import ReactDOM from "react-dom/client";
import { useTranslation } from "react-i18next";
import { MobileWarningModal } from "@/components/navigation/MobileWarningModal";
import { Toaster } from "@/components/ui/toaster";
import { isPublicRoute } from "@/config/routes";
import { useGlobalLogoutListener } from "@/hooks/useGlobalLogoutListener";
import { useLanguageSync } from "@/hooks/useLanguageSync";
import { queryClient } from "@/lib/queryClient";
import AuthProvider, { useAuthContext } from "@/providers/AuthProvider";
import { routeTree } from "@/routeTree.gen";

// ChunkLoadErrorの処理
const handleChunkError = (error: Error) => {
  console.error("Chunk load error detected:", error);

  if (
    error.name === "ChunkLoadError" ||
    error.message.includes("Loading chunk") ||
    error.message.includes("Failed to fetch dynamically imported module")
  ) {
    Object.keys(sessionStorage).forEach((key) => {
      if (key.includes("tanstack_router")) {
        sessionStorage.removeItem(key);
      }
    });

    window.location.reload();
    return;
  }

  throw error;
};

// カスタムエラーコンポーネント
const CustomErrorComponent: React.FC<{ error: Error }> = ({ error }) => {
  const { t } = useTranslation();

  useEffect(() => {
    handleChunkError(error);
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center font-primary">
      <div className="text-center">
        <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-blue-600 border-b-2" />
        <p className="text-gray-600">{t("common.loading")}</p>
      </div>
    </div>
  );
};

// ルーターインスタンスを作成
const router = createRouter({
  routeTree,
  context: {
    queryClient,
  },
  defaultErrorComponent: CustomErrorComponent,
  defaultPendingComponent: () => {
    const { t } = useTranslation();
    return (
      <div className="flex min-h-screen items-center justify-center font-primary">
        <div className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-blue-600 border-b-2" />
          <p className="text-gray-600">{t("common.loading")}</p>
        </div>
      </div>
    );
  },
});

// グローバルエラーハンドリング
window.addEventListener("unhandledrejection", (event) => {
  if (
    event.reason?.message?.includes("Failed to fetch dynamically imported module") ||
    event.reason?.name === "ChunkLoadError"
  ) {
    event.preventDefault();
    handleChunkError(event.reason);
  }
});

// 型安全性のためのモジュール拡張
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

// 認証が必要なルート用のラッパー
const AuthenticatedApp: React.FC = () => {
  const { isAuthReady } = useAuthContext();
  const { t } = useTranslation();

  // 各種フックを使用
  useGlobalLogoutListener();
  useLanguageSync();

  if (!isAuthReady) {
    return (
      <div className="flex min-h-screen items-center justify-center font-primary">
        <div className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-blue-600 border-b-2" />
          <p className="text-gray-600">{t("common.authorizing")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen font-primary">
      {" "}
      <MobileWarningModal />
      <RouterProvider router={router} />
      <Toaster />
    </div>
  );
};

// 認証不要ルート用のラッパー
const PublicApp: React.FC = () => {
  // 各種フックを使用
  useLanguageSync();

  return (
    <div className="min-h-screen font-primary">
      <RouterProvider router={router} />
      <Toaster />
    </div>
  );
};

// メインアプリケーション
const App: React.FC = () => {
  const currentPath = window.location.pathname;

  if (isPublicRoute(currentPath)) {
    return (
      <QueryClientProvider client={queryClient}>
        <PublicApp />
        {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
      </QueryClientProvider>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <AuthenticatedApp />
      </AuthProvider>
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
};

// アプリケーションをレンダリング
const rootElement = document.getElementById("root");
if (rootElement) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <StrictMode>
      <App />
    </StrictMode>,
  );
}
